import Image, { type StaticImageData } from "next/image";
import { cn } from "@workspace/ui/lib/utils";

export interface TestimonialCardProps {
  image: string | StaticImageData;
  imagePosition?: string;
  tags: string[];
  quote: string;
  name: string;
  meta: string;
  overlayVariant?: "dark" | "light";
  showTags?: boolean;
  className?: string;
  index?: number;
  idPrefix?: string;
}

export function TestimonialCard({
  image,
  imagePosition,
  tags,
  quote,
  name,
  meta,
  showTags = true,
  className,
  index,
  idPrefix,
}: TestimonialCardProps) {
  const loc = (part: string) =>
    idPrefix && index ? `${idPrefix}-${part}-${index}` : undefined;
  const primaryTag = tags[0];
  const overlayClassName =
    "bg-[linear-gradient(180deg,rgba(255,255,255,0.09)_0%,rgba(153,153,153,0.09)_100%)] backdrop-blur-[57.2289px]";

  return (
    <div
      id={loc("card")}
      data-testid={loc("card")}
      className={cn(
        "relative h-[502px] overflow-hidden rounded-2xl bg-[#232221] text-white lg:h-[580px] lg:rounded-[18px]",
        className,
      )}
    >
      <div className="relative h-[350px] w-full overflow-hidden rounded-t-2xl lg:h-[408px] lg:rounded-t-[18px]">
        <Image
          src={image}
          alt={name}
          fill
          className="pointer-events-none object-cover"
          style={imagePosition ? { objectPosition: imagePosition } : undefined}
          sizes="(min-width: 1024px) 412px, (min-width: 640px) 332px, 100vw"
        />
      </div>

      {showTags && primaryTag ? (
        <div
          id={loc("tag")}
          data-testid={loc("tag")}
          className="absolute left-4 top-5 z-10 rounded-[6.867px] bg-[#F3F2F1] px-[9.157px] py-[6.867px] captitle-12 text-primary lg:captitle-16"
        >
          {primaryTag}
        </div>
      ) : null}

      <div
        className={cn(
          "absolute inset-x-0 bottom-0 h-[190px] rounded-[16px] p-5 lg:h-[250px] lg:p-6",
          overlayClassName,
        )}
      >
        <div className="flex h-full flex-col">
          <p
            id={loc("quote")}
            data-testid={loc("quote")}
            className="min-h-0 flex-1 font-[family-name:var(--font-resist-text)] text-[14px] font-normal leading-[1.4285714286] tracking-[-0.0214285723em] text-white lg:text-[18px] lg:leading-[1.4444444444] lg:tracking-[-0.0277777778em]"
          >
            {quote}
          </p>

          <div className="mt-5 flex shrink-0 items-end justify-between gap-4 lg:mt-6">
            <span
              id={loc("name")}
              data-testid={loc("name")}
              className="font-[family-name:var(--font-resist-text)] text-[12px] font-semibold uppercase leading-[1.25] tracking-[0.02em] text-white lg:text-[12px] lg:leading-[1.25] lg:tracking-[0.02em]"
            >
              {name}
            </span>
            <span
              id={loc("location")}
              data-testid={loc("location")}
              className="text-right font-[family-name:var(--font-resist-text)] text-[12px] font-normal leading-[1.3333333333] tracking-[-0.0166666669em] text-white lg:text-[14px] lg:leading-[1.4285714286] lg:tracking-[-0.0214285723em]"
            >
              {meta}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
